import React, { useEffect, useCallback, useState } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import { Button, FormControlGroup, Typography, Input } from "src/components";
import { SoundPlayer } from "src/components/sound-recorder/player";
import { SoundRecorder } from "src/components/sound-recorder/recorder";
import {
  Header,
  GenderControl,
  Birthday,
  Address,
  Identification,
  DescriptionControl,
  AboutProfileControl,
  AboutTalkControl,
} from "./components";
import { useNavigation } from "@react-navigation/native";
import { useForm } from "react-hook-form";
import * as formValidators from "src/utils/form-validator";
import { IAddress, Gender } from "src/domains";
import {
  useAccount,
  useCognito,
  useAccountUpdateAction,
  useAccountUploadImageAction,
  useAccountUploadVoiceAction,
  useAccountIsNicknameExistAction,
  useAccountRemove,
} from "src/hooks";
import LocalText from "src/local";
import alert from "src/components/alert";
import {
  RootStackScreenProps,
  RootStackParamList,
  MainTabParamList,
} from "src/navigation/types";

import type { CompositeNavigationProp } from "@react-navigation/native";
import type { BottomTabNavigationProp } from "@react-navigation/bottom-tabs";
import type { StackNavigationProp } from "@react-navigation/stack";

type StackNavigationParam = {
  MyProfile: undefined;
};

type FormValues = {
  name: string;
  imageUrl?: string;
  fullname?: string;
  gender?: Gender;
  birthday?: string;
  address?: IAddress;
  aboutProfile?: string;
  aboutTalk?: string;
  description?: string;
  voice?: string;
};

type ProfileScreenNavigationProp = CompositeNavigationProp<
  BottomTabNavigationProp<MainTabParamList>,
  StackNavigationProp<RootStackParamList, "MyProfile">
>;

export const MyProfile: React.FC = () => {
  // const navigation =
  //   useNavigation<RootStackScreenProps<"MyProfile">["navigation"]>();
  const navigation = useNavigation<ProfileScreenNavigationProp>();
  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: { fullname: undefined },
  });
  const [account] = useAccount();
  const [isAccountLoadedToForm, setIsAccountLoadedToForm] =
    useState<boolean>(false);
  const [, cognitoAttributes] = useCognito();
  const [accountUploadImageAction] = useAccountUploadImageAction();
  const [accountUploadVoiceAction, accountUploadVoiceLoading] =
    useAccountUploadVoiceAction();
  const [accountUpdateAction, accountUpdateLoading] = useAccountUpdateAction();
  const [accountIsNicknameExistAction] = useAccountIsNicknameExistAction();
  const [isNameExist, setIsNameExist] = useState<boolean>(false);
  const [removeAccount, removeAccountLoading] = useAccountRemove();

  useEffect(() => {
    if (account && !isAccountLoadedToForm) {
      setValue("name", account.name);
      setValue("imageUrl", account.image);
      setValue("fullname", account.fullName);
      setValue("gender", account.gender);
      setValue("birthday", account.birthday);
      setValue("address", account.address);
      setValue("aboutProfile", account.aboutProfile);
      setValue("aboutTalk", account.aboutTalk);
      setValue("description", account.description);
      setValue("voice", account.voice);
      console.log("account.voice", account.voice);
      setIsAccountLoadedToForm(true);
    }
  }, [account, isAccountLoadedToForm]);

  useEffect(() => {
    register("name", {
      validate: (value) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.myProfile.nameValidationRequired;
        }
        return true;
      },
    });
  }, [register]);

  const onSave = useCallback(
    async (data: FormValues) => {
      if (isNameExist) {
        return;
      }
      if (
        await accountUpdateAction({
          input: {
            name: data.name,
            image: data.imageUrl,
            fullName: data.fullname,
            gender: data.gender,
            birthday: data.birthday,
            addressPostnumber: data.address?.postnumber,
            addressPrefecture: data.address?.prefecture,
            addressCity: data.address?.city,
            address: data.address?.address,
            address2: data.address?.address2,
            address3: data.address?.address3,
            aboutTalk: data.aboutTalk,
            aboutProfile: data.aboutProfile,
            description: data.description,
            voice: data.voice,
          },
        })
      ) {
        navigation.goBack();
      }
    },
    [navigation, isNameExist]
  );

  const onNameChange = useCallback(async () => {
    const name = getValues("name");
    console.log("onNameChange", name);
    if (name.length > 0) {
      const response = await accountIsNicknameExistAction({ nickname: name });
      console.log("response", response, response !== false);
      setIsNameExist(response !== false);
    } else {
      setIsNameExist(false);
    }
  }, [getValues]);

  const removeAccountHandler = useCallback(() => {
    alert(
      LocalText.screens.myPage.removeAccountAlertTitle,
      LocalText.screens.myPage.removeAccountAlertMessage,
      [
        { text: LocalText.common.cancel, style: "cancel" },
        {
          text: LocalText.screens.myPage.removeAccountAlertAction,
          style: "destructive",
          onPress: async () => {
            await removeAccount();
            window.open("/", "_self");
          },
        },
      ],
      { cancelable: false }
    );
  }, [removeAccount, navigation]);
  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView}>
        <Header
          style={styles.header}
          imageSource={
            getValues("imageUrl") ? { uri: getValues("imageUrl") } : undefined
          }
          name={account?.name}
          email={cognitoAttributes?.email}
          loading={accountUpdateLoading}
          onChangeImage={async (image) => {
            if (image) {
              const url = await accountUploadImageAction(image);
              setValue("imageUrl", url, { shouldValidate: true });
            } else {
              setValue("imageUrl", undefined, { shouldValidate: true });
            }
          }}
        />
        <Typography
          text={LocalText.screens.myProfile.generalSection}
          variant={"section"}
        />
        <FormControlGroup>
          <Input
            label={LocalText.screens.myProfile.nickname}
            placeholder={LocalText.screens.myProfile.nicknamePlaceholder}
            name={"name"}
            control={control}
            error={
              errors.name?.message ||
              (isNameExist
                ? LocalText.screens.myProfile.nicknameValidationExist
                : undefined)
            }
            autoCapitalize={"none"}
            maxLength={256}
            onBlur={() => onNameChange()}
          />
          <Input
            label={LocalText.screens.myProfile.fullname}
            placeholder={LocalText.screens.myProfile.fullnamePlaceholder}
            helper={LocalText.screens.myProfile.fullnameHelper}
            name={"fullname"}
            control={control}
            error={errors.fullname?.message}
            autoCapitalize={"none"}
          />
          <GenderControl
            value={getValues("gender")}
            error={errors.gender?.message}
            onChange={(value) =>
              setValue("gender", value, { shouldValidate: true })
            }
          />
          <Birthday
            value={getValues("birthday")}
            error={errors.birthday?.message}
            onChange={(value) => {
              setValue("birthday", value, { shouldValidate: true });
            }}
          />
          <Address
            value={getValues("address")}
            error={errors.address?.message}
            onChange={(value) =>
              setValue("address", value, { shouldValidate: true })
            }
          />
          <Typography
            text={LocalText.screens.myProfile.identificationSection}
            variant={"section"}
          />
          <Identification
            value={account?.identificationStatus}
            onFetchValues={() => ({
              isFullnameFilled: !!getValues("fullname")?.length,
              isGenderFilled: !!getValues("gender")?.length,
              isBirthdayFilled: !!getValues("birthday"),
              isAddressFilled: !!getValues("address")?.postnumber,
              isDirty: isDirty,
            })}
          />
          <Typography
            text={LocalText.screens.myProfile.profileSection}
            variant={"section"}
          />
          <AboutProfileControl
            control={control}
            error={errors.aboutProfile?.message}
          />
          <Typography
            text={LocalText.screens.myProfile.aboutTalkSection}
            variant={"section"}
          />
          <AboutTalkControl
            control={control}
            error={errors.aboutTalk?.message}
          />
          <Typography
            text={LocalText.screens.myProfile.descriptionSection}
            variant={"section"}
          />
          <DescriptionControl
            control={control}
            error={errors.description?.message}
          />
          {/* <Typography
            text={LocalText.screens.myProfile.soundSection}
            variant={"section"}
          /> */}
          {/* <SoundRecorder
            variant={SoundRecoredVariant.Recorder}
            source={getValues("voice")}
            onChange={async (file) => {
              if (file) {
                const url = await accountUploadVoiceAction(file);
                setValue("voice", url, { shouldValidate: true });
              } else {
                setValue("voice", undefined, { shouldValidate: true });
              }
            }}
            loading={accountUploadVoiceLoading}
          /> */}
          {/* <SoundPlayer source={getValues("voice")} /> */}
          {/* <SoundRecorder
            onChange={async (file) => {
              if (file) {
                const url = await accountUploadVoiceAction(file);
                setValue("voice", url, { shouldValidate: true });
              } else {
                setValue("voice", undefined, { shouldValidate: true });
              }
            }}
            loading={accountUploadVoiceLoading}
          /> */}
        </FormControlGroup>
        <View style={styles.removeAccountContainer}>
          <Button
            containerStyle={styles.removeAccountBtn}
            loading={removeAccountLoading}
            variant={"secondary"}
            title={LocalText.screens.myPage.removeAccountBtn}
            onPress={removeAccountHandler}
          />
        </View>
      </ScrollView>
      <View style={styles.actionContainer}>
        <Button
          containerStyle={styles.chatActionContainer}
          variant={"secondary"}
          icon={"cancel"}
          iconColor={colors.chokuRed}
          title={LocalText.common.cancel}
          onPress={() => navigation.goBack()}
        />
        <Button
          containerStyle={styles.offerActionContainer}
          loading={accountUpdateLoading}
          variant={"primary"}
          icon={"check"}
          title={LocalText.common.save}
          onPress={handleSubmit(onSave)}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollView: {
    backgroundColor: colors.gray1,
  },
  header: {
    marginTop: 4,
  },
  actionContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 16,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    backgroundColor: colors.white,
  },
  chatActionContainer: {
    flex: 1,
    marginRight: 8,
  },
  offerActionContainer: {
    flex: 1,
    marginLeft: 8,
  },
  detail: {
    color: colors.chokuBlack,
  },
  removeAccountContainer: {
    margin: 16,
  },
  removeAccountBtn: {
    flex: 1,
  },
});
